import { InputAdornment, TextField, TextFieldProps } from '@mui/material';
import { ChangeEvent, FormEvent, useState } from 'react';

// Mock currency utils for now - this should be provided by the consuming app
const getCurrencySymbol = (currency: string) => {
  const currencySymbols: Record<string, string> = {
    EUR: '€',
    USD: '$',
    GBP: '£',
    PLN: 'zł',
    CHF: 'CHF',
    NOK: 'kr',
    SEK: 'kr',
    DKK: 'kr',
    CAD: 'C$',
    AUD: 'A$',
    JPY: '¥',
    CNY: '¥',
  };

  return <span data-testid="currency-symbol">{currencySymbols[currency] || currency}</span>;
};

// Get locale based on currency
const getCurrencyLocale = (currency: string): string => {
  switch (currency) {
    case 'EUR':
      return 'de-DE'; // Period for thousands, comma for decimals (1.000.000,00)
    case 'GBP':
    case 'USD':
    default:
      return 'en-US'; // Comma for thousands, period for decimals (1,000,000.00)
  }
};

const doesLocaleUseCommaAsDecimalSeparator = (locale: string): boolean => {
  return locale === 'de-DE' || locale === 'nl-NL';
};

const formatNumberWithLocale = (value: number | string, locale = 'en-US', forceFormat = false): string => {
  if (value === '' || value === null || value === undefined) {
    return '';
  }

  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(numValue)) {
    return '';
  }

  if (!forceFormat && numValue % 1 === 0) {
    return numValue.toString();
  }

  if (forceFormat) {
    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(numValue);
  }

  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(numValue);
};

const parseNumberFromString = (value: string): number => {
  if (!value) {
    return 0;
  }

  const cleanValue = value.replace(/[^\d,.-]/g, '');
  const normalizedValue = cleanValue.replace(',', '.');
  return parseFloat(normalizedValue) || 0;
};

export interface CurrencyNumberInputProps extends Omit<TextFieldProps, 'onChange' | 'value'> {
  value?: number;
  onChange: (value: number) => void;
  currency: string;
  size?: TextFieldProps['size'];
}

export const CurrencyNumberInput = ({ 
  value, 
  onChange, 
  currency, 
  size = 'small', 
  ...textFieldProps 
}: CurrencyNumberInputProps) => {
  const [rawInputValue, setRawInputValue] = useState<string>('');
  const [isFocused, setIsFocused] = useState<boolean>(false);
  
  // Get locale based on currency instead of browser locale
  const currentLocale = getCurrencyLocale(currency);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setRawInputValue(e.target.value);
    const parsedValue = parseNumberFromString(e.target.value);
    onChange(parsedValue);
  };

  const handleFocus = () => {
    setIsFocused(true);
    if (value !== undefined && value !== null) {
      setRawInputValue(formatNumberWithLocale(value, currentLocale, false));
    } else {
      setRawInputValue('');
    }
  };

  const handleBlur = () => {
    setIsFocused(false);
    setRawInputValue('');
  };

  const handleInput = (e: FormEvent<HTMLInputElement>) => {
    const input = e.target as HTMLInputElement;
    // Only block dots if the locale uses comma as decimal separator
    if (doesLocaleUseCommaAsDecimalSeparator(currentLocale)) {
      if (input.value.includes('.')) {
        input.value = input.value.replace(/\./g, ',');
      }
    }
  };

  return (
    <TextField
      data-testid="currency-number-input"
      size={size}
      {...textFieldProps}
      value={
        isFocused
          ? rawInputValue
          : value !== undefined && value !== null
            ? formatNumberWithLocale(value, currentLocale, true)
            : ''
      }
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onInput={handleInput}
      type="text"
      inputProps={{
        inputMode: 'decimal',
        ...textFieldProps.inputProps,
      }}
      InputProps={{
        startAdornment: <InputAdornment position="start">{getCurrencySymbol(currency)}</InputAdornment>,
        ...textFieldProps.InputProps,
      }}
      placeholder={doesLocaleUseCommaAsDecimalSeparator(currentLocale) ? '0,00' : '0.00'}
    />
  );
};
